import RecommendIcon from '@mui/icons-material/Recommend';
import { Box, Stack } from '@mui/material';
import Autocomplete, {
  type AutocompleteChangeReason,
  type AutocompleteInputChangeReason,
  type AutocompleteValue,
} from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import deburr from 'lodash.deburr';
import {
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from 'react-hook-form';

import { type DictionaryLanguageDto } from '@/api/generated/models/dictionary-language-dto';
import { useFormProviderKnowMePropsContext } from '@/components/form/FormProviderKnowMe.comp';
import {
  type SelectOptionIdType,
  type SelectSearchOptionModel,
} from '@/components/form/model/SelectSearchOption.model.ts';
import { handleKeyDownDefault } from '@/components/form/util/handleKeyDownDefault.util.ts';
import { FormSelectCustomOptionsEnum } from '@/components/ui/formSelect/FormSelectCustomOptions.enum.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';
import { getAppLanguage } from '@/utils/getAppLanguage.util.ts';
import { getSelectOptionLabel } from '@/utils/getDictionaryLabel.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import { type FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

export type SelectSearchFormFieldOnChangeHandler<
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
  LABEL_TYPE extends ReactNode = string,
> = (
  newValue: SelectSearchFormFieldValue<ID_TYPE, IS_MULTI, LABEL_TYPE>,
  reason: AutocompleteChangeReason,
) => void;

export type SelectSearchFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  IS_MULTI extends boolean = false,
  ID_TYPE extends SelectOptionIdType = number,
  LABEL_TYPE extends ReactNode = string,
> = FormPropsModel<TName> &
  BasicProps<ID_TYPE, IS_MULTI, LABEL_TYPE> &
  (
    | {
        isCreatable: true;
        onCreate: (inputValue: string) => void;
      }
    | {
        isCreatable?: false;
        onCreate?: undefined;
      }
  ) &
  (
    | {
        isAsync: true;
        loadOptions: (
          inputValue: string,
        ) => Promise<SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>[]>;
        options?: undefined;
      }
    | {
        isAsync?: false;
        loadOptions?: undefined;
        options: ReadonlyArray<SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>>;
      }
  );

export type SelectSearchFormFieldValue<
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
  LABEL_TYPE extends ReactNode = string,
> = IS_MULTI extends true
  ? SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>[]
  : SelectSearchOptionModel<ID_TYPE, LABEL_TYPE> | null;

export type SelectSearchInputActionMeta = {
  action: AutocompleteInputChangeReason;
};

type AutocompleteOption<ID_TYPE extends SelectOptionIdType, LABEL_TYPE extends ReactNode = string> =
  CreateOption | SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>;

type BasicProps<
  ID_TYPE extends SelectOptionIdType = number,
  IS_MULTI extends boolean = false,
  LABEL_TYPE extends ReactNode = string,
> = {
  closeMenuOnSelect?: boolean;
  customOptions?: FormSelectCustomOptionsEnum;
  formatCreateLabel?: (inputValue: string) => ReactElement;
  hasNextPage?: boolean;
  hideSelectedOptions?: boolean;
  isClearable?: boolean;
  isCreating?: boolean;
  isLoadingOptions?: boolean;
  isMulti?: IS_MULTI;
  isNotClearable?: boolean;
  isOptionDisabled?: (option: SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>) => boolean;
  isSearchable?: boolean;
  isServerFiltered?: boolean;
  menuPlacement?: MenuPlacement;
  onChange?: SelectSearchFormFieldOnChangeHandler<ID_TYPE, IS_MULTI, LABEL_TYPE>;
  onInputChange?: (newValue: string, meta: SelectSearchInputActionMeta) => void;
  onMenuScrollToBottom?: () => void;
  overwriteLanguage?: DictionaryLanguageDto;
  renderOptionLabel?: (option: SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>) => ReactNode;
  startIcon?: ReactNode;
};

type CreateOption = { inputValue: string };

type MenuPlacement = 'auto' | 'bottom' | 'top';

const isCreateOption = (option: unknown): option is CreateOption => {
  return typeof option === 'object' && option !== null && 'inputValue' in option;
};

const getCreatedValue = (value: unknown): string | undefined => {
  const values = Array.isArray(value) ? value : [value];
  const createdOption = values.find(option => typeof option === 'string' || isCreateOption(option));

  if (typeof createdOption === 'string') {
    return createdOption;
  }

  return isCreateOption(createdOption) ? createdOption.inputValue : undefined;
};

const getPopperPlacement = (menuPlacement?: MenuPlacement) => {
  if (menuPlacement === 'top') {
    return 'top-start' as const;
  }
  if (menuPlacement === 'bottom') {
    return 'bottom-start' as const;
  }
  return undefined;
};

const VacancyCompanyOption: React.FC<{
  data: SelectSearchOptionModel<number>;
  label: string;
}> = props => {
  const { data, label } = props;

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Stack direction="row">
        <span
          style={{
            color: data.isRecommendedStatus ? themeColors.blue : 'inherit',
            fontWeight: data.isRecommendedStatus ? 'bold' : 'normal',
          }}
        >
          {label}
        </span>
      </Stack>
      {data.isRecommendedStatus && (
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            backgroundColor: themeColors.lightGreen,
            borderRadius: '4px',
            color: themeColors.blue,
            display: 'flex',
            fontSize: '0.75rem',
            marginLeft: '8px',
            padding: '2px 6px',
          }}
        >
          <RecommendIcon sx={{ fontSize: '0.875rem', marginRight: '2px' }} />
        </Box>
      )}
    </Stack>
  );
};

export const SelectSearchFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  IS_MULTI extends boolean = false,
  ID_TYPE extends SelectOptionIdType = number,
  LABEL_TYPE extends ReactNode = string,
>(
  props: SelectSearchFormFieldProps<TFieldValues, TName, IS_MULTI, ID_TYPE, LABEL_TYPE>,
): ReactElement<SelectSearchFormFieldProps<TFieldValues, TName, IS_MULTI, ID_TYPE, LABEL_TYPE>> => {
  const {
    closeMenuOnSelect,
    customOptions,
    disabled,
    formatCreateLabel,
    fullWidth,
    hasNextPage,
    hideSelectedOptions,
    id,
    isAsync,
    isClearable,
    isCreatable,
    isCreating,
    isLoadingOptions,
    isMulti,
    isNotClearable,
    isOptionDisabled,
    isSearchable,
    isServerFiltered,
    label,
    menuPlacement,
    name,
    onChange,
    onCreate,
    onInputChange,
    onMenuScrollToBottom,
    overwriteLanguage,
    placeholder,
    renderOptionLabel,
    startIcon,
    sx,
    title,
  } = props;
  const language = overwriteLanguage ?? getAppLanguage();
  const { control } = useFormContext<TFieldValues>();
  const { validation } = useFormProviderKnowMePropsContext();
  const { t } = useTranslationWithPrefix('components.form.select');
  const [asyncOptions, setAsyncOptions] = useState<SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>[]>(
    [],
  );
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const hasLoadedAsyncOptionsRef = useRef(false);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const controlRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!isMulti) {
      return;
    }
    const control = controlRef.current;
    if (!control) {
      return;
    }
    const autocompleteRoot = control.querySelector('.MuiAutocomplete-root');
    if (autocompleteRoot?.classList.contains('Mui-focused')) {
      return;
    }
    control.style.minHeight = `${control.offsetHeight}px`;
  }, [isMulti]);

  const normalizedMultiValueRef = useRef<{ normalized: unknown[]; raw: unknown }>({
    normalized: [],
    raw: undefined,
  });

  const resolveLabelString = (option: SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>): string => {
    const resolved = getSelectOptionLabel({ language, option });
    return typeof resolved === 'string' ? resolved : '';
  };

  const loadAsyncOptions = (nextInputValue: string) => {
    if (!isAsync || !('loadOptions' in props) || !props.loadOptions) {
      return;
    }
    hasLoadedAsyncOptionsRef.current = true;
    const currentRequestId = ++requestIdRef.current;
    setIsLoading(true);
    void props
      .loadOptions(nextInputValue)
      .then(newOptions => {
        if (currentRequestId === requestIdRef.current) {
          setAsyncOptions(newOptions);
        }
      })
      .catch(() => {
        if (currentRequestId === requestIdRef.current) {
          setAsyncOptions([]);
        }
      })
      .finally(() => {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
  };

  const checkListNeedsMore = () => {
    const node = listboxRef.current;
    if (node && node.scrollHeight <= node.clientHeight) {
      onMenuScrollToBottom?.();
    }
  };

  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const isRequired = isFormFieldRequired(field.name, validation);
    const sourceOptions: ReadonlyArray<SelectSearchOptionModel<ID_TYPE, LABEL_TYPE>> = isAsync
      ? asyncOptions
      : 'options' in props && props.options
        ? props.options
        : [];

    const preparedOptions = isServerFiltered
      ? sourceOptions
      : sourceOptions
          .filter(option => option.active !== false)
          .toSorted((a, b) => {
            if (a.isFirst !== b.isFirst) {
              return a.isFirst ? -1 : 1;
            }
            return resolveLabelString(a).localeCompare(resolveLabelString(b));
          });

    let value: AutocompleteValue<
      AutocompleteOption<ID_TYPE, LABEL_TYPE>,
      IS_MULTI,
      boolean,
      boolean
    >;

    if (isMulti) {
      const fieldValue: unknown = field.value;
      if (normalizedMultiValueRef.current.raw !== fieldValue) {
        let normalized: unknown[];
        if (!Array.isArray(fieldValue) || fieldValue.length === 0) {
          normalized = [];
        } else {
          normalized = fieldValue.filter(option => option != null);
        }
        normalizedMultiValueRef.current = {
          normalized,
          raw: fieldValue,
        };
      }
      value = normalizedMultiValueRef.current.normalized as AutocompleteValue<
        AutocompleteOption<ID_TYPE, LABEL_TYPE>,
        IS_MULTI,
        boolean,
        boolean
      >;
    } else {
      value = (field.value ?? null) as AutocompleteValue<
        AutocompleteOption<ID_TYPE, LABEL_TYPE>,
        IS_MULTI,
        boolean,
        boolean
      >;
    }

    const getOptionLabel = (option: AutocompleteOption<ID_TYPE, LABEL_TYPE> | string): string => {
      if (!option) {
        return '';
      }
      if (typeof option === 'string') {
        return option;
      }
      if (isCreateOption(option)) {
        return option.inputValue;
      }
      const resolved = resolveLabelString(option);
      if (!resolved && 'value' in option) {
        const found = sourceOptions.find(o => o.value === option.value);
        if (found) {
          return resolveLabelString(found);
        }
      }
      return resolved;
    };

    const matchesInput = (
      option: AutocompleteOption<ID_TYPE, LABEL_TYPE>,
      input: string,
    ): boolean => {
      if (isCreateOption(option)) {
        return true;
      }
      const trimmedInput = input.trim();
      if (!trimmedInput) {
        return true;
      }
      const normalized = deburr(trimmedInput).toLowerCase();

      const labelString = resolveLabelString(option);
      if (deburr(labelString).toLowerCase().includes(normalized)) {
        return true;
      }

      if (typeof option.label === 'object' && option.label !== null) {
        for (const locLabel of Object.values(option.label)) {
          if (typeof locLabel === 'string' && deburr(locLabel).toLowerCase().includes(normalized)) {
            return true;
          }
        }
      }

      const search = option.search;
      if (typeof search === 'string') {
        return deburr(search).toLowerCase().includes(normalized);
      }
      if (search && typeof search === 'object') {
        for (const searchVal of Object.values(search)) {
          if (
            typeof searchVal === 'string' &&
            deburr(searchVal).toLowerCase().includes(normalized)
          ) {
            return true;
          }
        }
      }
      return false;
    };

    const handleInputChange = (
      _event: SyntheticEvent,
      newInputValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      const nextInputValue = typeof newInputValue === 'string' ? newInputValue : '';
      setInputValue(nextInputValue);
      onInputChange?.(nextInputValue, { action: reason });

      if (isAsync && reason === 'input') {
        loadAsyncOptions(nextInputValue);
      }
    };

    const handleOnChange = (
      _event: SyntheticEvent,
      newValue: AutocompleteValue<
        AutocompleteOption<ID_TYPE, LABEL_TYPE>,
        IS_MULTI,
        boolean,
        boolean
      >,
      reason: AutocompleteChangeReason,
    ) => {
      const createdValue = getCreatedValue(newValue);
      if (createdValue !== undefined) {
        setInputValue('');
        onCreate?.(createdValue);
        return;
      }

      if (isMulti) {
        const selectedValues = (Array.isArray(newValue) ? newValue : []).filter(
          (option): option is SelectSearchOptionModel<ID_TYPE, LABEL_TYPE> =>
            !isCreateOption(option) && typeof option !== 'string',
        );
        onChange?.(
          selectedValues as SelectSearchFormFieldValue<ID_TYPE, IS_MULTI, LABEL_TYPE>,
          reason,
        );
        field.onChange(selectedValues);
        return;
      }

      const selectedValue =
        !Array.isArray(newValue) && !isCreateOption(newValue) && typeof newValue !== 'string'
          ? newValue
          : null;
      onChange?.(
        selectedValue as SelectSearchFormFieldValue<ID_TYPE, IS_MULTI, LABEL_TYPE>,
        reason,
      );
      field.onChange(selectedValue);
    };

    const handleOpen = () => {
      if (isAsync && !hasLoadedAsyncOptionsRef.current) {
        loadAsyncOptions(inputValue);
      }
      if (onMenuScrollToBottom) {
        window.setTimeout(checkListNeedsMore, 0);
      }
    };

    const handleListboxScroll = (event: SyntheticEvent<HTMLUListElement>) => {
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 1) {
        onMenuScrollToBottom?.();
      }
    };

    const noOptionsText =
      (isAsync || isServerFiltered) && !inputValue ? t('startTyping') : t('noOptionsMessage');
    const hasSelectedValue = isMulti ? Array.isArray(value) && value.length > 0 : value != null;
    const resolvedPlaceholder = placeholder ?? (typeof label === 'string' ? label : undefined);

    return (
      <FormControl
        fullWidth={fullWidth ?? true}
        ref={controlRef}
        sx={{
          '& .MuiAutocomplete-root .MuiAutocomplete-inputRoot': {
            flexWrap: isMulti ? 'wrap' : 'nowrap',
          },
          height: '100%',
          position: 'relative',
          ...(isMulti && {
            '& .MuiAutocomplete-root.Mui-focused': {
              backgroundColor: 'background.paper',
              height: 'auto',
              left: 0,
              position: 'absolute',
              top: 0,
              width: '100%',
              zIndex: 1400,
            },
          }),
        }}
      >
        <Autocomplete<AutocompleteOption<ID_TYPE, LABEL_TYPE>, IS_MULTI, boolean, boolean>
          aria-label={name}
          disableClearable={isNotClearable === true || isClearable === false}
          disableCloseOnSelect={closeMenuOnSelect === false}
          disabled={disabled}
          filterOptions={(availableOptions, state) => {
            const shouldSkipClientFilter =
              isSearchable === false || isServerFiltered === true || isAsync === true;
            const filteredOptions = shouldSkipClientFilter
              ? availableOptions
              : availableOptions.filter(option => matchesInput(option, state.inputValue));
            const trimmedInput = state.inputValue.trim();
            const normalizedInputValue = deburr(trimmedInput).toLowerCase();
            const hasMatchingOption = availableOptions.some(
              option =>
                !isCreateOption(option) &&
                deburr(resolveLabelString(option).trim()).toLowerCase() === normalizedInputValue,
            );

            if (!isCreatable || !normalizedInputValue || hasMatchingOption || hasNextPage) {
              return filteredOptions;
            }

            return [...filteredOptions, { inputValue: state.inputValue }];
          }}
          filterSelectedOptions={hideSelectedOptions ?? Boolean(isMulti)}
          freeSolo={isCreatable}
          fullWidth={fullWidth ?? true}
          getOptionDisabled={option =>
            !isCreateOption(option) && (isOptionDisabled?.(option) ?? false)
          }
          getOptionKey={option => {
            if (typeof option === 'string') {
              return option;
            }
            if (isCreateOption(option)) {
              return `__create__:${option.inputValue}`;
            }
            if (option.value != null) {
              return String(option.value);
            }
            return resolveLabelString(option);
          }}
          getOptionLabel={getOptionLabel}
          id={id}
          isOptionEqualToValue={(option, optionValue) => {
            if (typeof option === 'string' || isCreateOption(option) || optionValue == null) {
              return false;
            }
            const candidateValue =
              typeof optionValue === 'object' && 'value' in optionValue
                ? (optionValue as SelectSearchOptionModel<ID_TYPE>).value
                : optionValue;
            return option.value === candidateValue;
          }}
          limitTags={isMulti ? 1 : -1}
          loading={isLoading || isLoadingOptions === true || isCreating === true}
          loadingText={t('loading')}
          multiple={isMulti}
          noOptionsText={noOptionsText}
          onBlur={field.onBlur}
          onChange={handleOnChange}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDownDefault({ allowEnterForSelect: true })}
          onOpen={handleOpen}
          openOnFocus
          options={preparedOptions}
          readOnly={isSearchable === false}
          renderInput={params => (
            <TextField
              {...params}
              error={Boolean(error)}
              inputRef={field.ref}
              label={label}
              name={field.name}
              placeholder={hasSelectedValue ? undefined : resolvedPlaceholder}
              size="small"
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: startIcon ? (
                    <>
                      {startIcon}
                      {params.slotProps.input?.startAdornment}
                    </>
                  ) : (
                    params.slotProps.input?.startAdornment
                  ),
                },
                inputLabel: {
                  ...params.slotProps.inputLabel,
                  required: isRequired,
                  shrink: true,
                },
              }}
              variant="standard"
            />
          )}
          renderOption={(optionProps, option) => {
            const { key, ...optionPropsWithoutKey } = optionProps;
            if (isCreateOption(option)) {
              return (
                <li key={key} {...optionPropsWithoutKey}>
                  {formatCreateLabel?.(option.inputValue) ??
                    t('createItem', { item: option.inputValue })}
                </li>
              );
            }

            const resolvedLabel = resolveLabelString(option);

            if (renderOptionLabel) {
              return (
                <li key={key} {...optionPropsWithoutKey}>
                  {renderOptionLabel(option)}
                </li>
              );
            }

            if (customOptions === FormSelectCustomOptionsEnum.VACANCY_COMPANY) {
              return (
                <li key={key} {...optionPropsWithoutKey}>
                  <VacancyCompanyOption
                    data={option as SelectSearchOptionModel<number>}
                    label={resolvedLabel}
                  />
                </li>
              );
            }

            return (
              <li key={key} {...optionPropsWithoutKey}>
                {resolvedLabel}
              </li>
            );
          }}
          slotProps={{
            listbox: {
              onScroll: handleListboxScroll,
              ref: (node: HTMLUListElement | null) => {
                listboxRef.current = node;
              },
              sx: { maxHeight: 200, p: 0.5 },
            },
            popper: {
              placement: getPopperPlacement(menuPlacement),
              style: { zIndex: 1500 },
            },
          }}
          sx={sx}
          title={title}
          value={value}
        />
        <TextError error={error} />
      </FormControl>
    );
  };

  return <Controller control={control} name={name} render={render} />;
};
